"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/EmailLogin/layouts/LoginLayouts";

import React from "react";
import { AppImage } from "@components/NextImage";
import { Icon } from "@ospweb/sdk-ui";
import {
  WELCOME_ASSETS,
  WELCOME_H5_BACKGROUND_OVERLAY,
} from "@components/OnboardingWelcome/welcomeLayoutTokens";
import type { EmailLoginStep } from "../types";

const I18n = I18nUtil.createScoped(Translations);

interface DesktopLoginLayoutProps {
  children: React.ReactNode;
}

export function DesktopLoginLayout({ children }: DesktopLoginLayoutProps) {
  return (
    <div className="flex h-full flex-col gap-2 md:items-center md:justify-between">
      <AppImage
        src="/edgen_logo_full_v2.svg"
        alt="Aura Logo"
        width={56}
        height={28}
        className="mt-2 hidden h-auto md:block"
      />
      {children}
    </div>
  );
}

interface MobileLoginLayoutProps {
  step: EmailLoginStep;
  onBackToEmail: () => void;
  children: React.ReactNode;
}

export function MobileLoginLayout({
  step,
  onBackToEmail,
  children,
}: MobileLoginLayoutProps) {
  if (step === "email") {
    return (
      <div
        className="relative flex h-dvh w-full flex-col overflow-hidden bg-background_solid_neutral_secondary"
        data-dismiss-kbd-scope="1"
      >
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden
        >
          <AppImage
            src={WELCOME_ASSETS.h5Background}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            width={393}
            height={852}
            loading="eager"
            decoding="async"
          />
          <div
            className="absolute inset-0"
            style={{ backgroundImage: WELCOME_H5_BACKGROUND_OVERLAY }}
          />
        </div>
        <div className="relative z-10 min-h-0 w-full flex-1">
          <div className="absolute inset-x-0 top-[48%] flex -translate-y-1/2 flex-col items-center px-8">
            <AppImage
              src="/assets/images/logo-white.svg"
              alt="EdWealth"
              width={75}
              height={40}
              loading="eager"
            />
            <p className="headline_regular_m mt-10 text-center text-content_aura_lv3_primary">
              {I18n.t("The money person you've")}
              <br />
              {I18n.t("been doing without")}
            </p>
          </div>
        </div>
        <div className="relative z-10 -mt-8 flex w-full shrink-0 flex-col rounded-t-[24px] bg-background_solid_neutral_secondary px-5 pt-6 pb-[max(40px,env(safe-area-inset-bottom,0px))]">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-dvh w-full flex-col overflow-hidden bg-background_solid_neutral_secondary pt-safe"
      data-dismiss-kbd-scope="1"
    >
      <header className="shrink-0 px-4 pb-3 pt-3">
        <button
          type="button"
          aria-label="Back to email"
          data-openreplay-label="login_loginlayouts_back"
          onClick={onBackToEmail}
          className="flex size-10 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]"
        >
          <Icon
            name="left-arrow-alt"
            size={20}
            className="text-content_primary"
          />
        </button>
      </header>
      <div className="flex min-h-0 flex-1 flex-col px-4 pt-5">{children}</div>
    </div>
  );
}
